import React from "react"
import {Box, Skeleton, Typography} from "@mui/material"
import { AmchartSeeker } from "@ui-seeker/core"
import { format, parse } from "date-fns"

import AnalyzerEvolutionHeader from "./Header"
import { evolutionChartVisibility, openEvolutionModal } from "Pages/AnalyzersV2/@signals"
import AnalyzerEvolutionChartActions from "Pages/AnalyzersV2/@components/EvolutionModal/ChartActions"
import {reports, searchID} from "../../../../@signals";
import {AppOfficesBlock} from "../../../../@components";
import { POSITION_MAIN_COLOR } from "Pages/AnalyzersV2/@constants"
import { isRankEvolutionData } from "Pages/AnalyzersV2/@components/EvolutionModal/@utils"
import {
	buildRankData,
	findMainDataKey,
	getMaxRank,
	priceField
} from "Pages/AnalyzersV2/@components/RankEvolutionChart/@utils"


// The execution evolution sends `date` as yyyyMMddHHmm: one category per execution, evenly spaced
const EXECUTION_DATE_RECEIVED = 'yyyyMMddHHmm'
const EXECUTION_DATE_LABEL = 'dd MMM yyyy HH:mm'

const toExecutionLabel = date => format(parse(date, EXECUTION_DATE_RECEIVED, new Date()), EXECUTION_DATE_LABEL)

export default function AnalyzerEvolutionChart({
	xTitle, isLoading, chartData, keySeries, isRating, setIsVisible, setIsLoading, executionDateTime, officeData,
	isByExecution, canGroupByExecution, toggleByExecution
}) {
	const { tableRows } = openEvolutionModal.value
	if (isLoading) return <Box>
		{tableRows?.length >= 2 && !isRating && <Skeleton variant="rectangular" height={75} sx={{ mb: .5 }}/>}
		{tableRows?.length >= 3 && !isRating && <Skeleton variant="rectangular" height={75} sx={{ mb: .5 }}/>}
		{tableRows?.length >= 4 && !isRating && <Skeleton variant="rectangular" height={75} sx={{ mb: .5 }}/>}
		{!isRating && <Skeleton variant="rectangular" height={75} sx={{ mb: .5 }}/>}
		<Skeleton variant="rectangular" height='78vh' sx={{ mb: .5 }}/>
	</Box>

	// The position analyzer plots market rank instead of price, so its evolution
	// reads the same way as its "Rank evolution" chart.
	const isRankChart = isRankEvolutionData(chartData)
	const dataKeys = keySeries?.map(({ dataKey }) => dataKey) || []
	const evolutionData = isRankChart ? buildRankData(chartData, dataKeys) : chartData
	const data = isByExecution ? evolutionData.map(row => ({ ...row, date: toExecutionLabel(row.date) })) : evolutionData
	const mainKey = isRankChart ? findMainDataKey(chartData, dataKeys) : null
	const maxRank = isRankChart ? getMaxRank(data, dataKeys) : null

	return <AmchartSeeker
		title={<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }} >
			<Typography variant='h4'>Evolution Chart</Typography>
			{!!searchID.value || reports.value && <>
				<AppOfficesBlock {...officeData} /> -
				<Typography variant="subtitle2" fontWeight='regular'>{executionDateTime}</Typography>
			</>}
		</Box>}
		rootID="ChartEvolutionSelected"
		data={data}
		sx={{ height: '78vh' }}
		series={keySeries?.map(({ dataKey, color }, index) => ({
			name: dataKey,
			yField: dataKey,
			xField: 'date',
			color: isRankChart && dataKey === mainKey ? POSITION_MAIN_COLOR : color,
			hidden: !evolutionChartVisibility.value.includes(dataKey),
			// A missing rank means no rate that day, so the line has to break.
			connect: !isRankChart,
			tension: 1,
			...isRankChart && {
				strokes: { strokeWidth: dataKey === mainKey ? 4 : 2 },
				tooltip: {
					pointerOrientation: 'horizontal',
					labelText: `{name}: rank {valueY.formatNumber("#")} · {${priceField(index)}}`,
				},
			},
		}))}
		extraHeader={<AnalyzerEvolutionHeader isRating={isRating} />}
		cardProps={{
			actions: <AnalyzerEvolutionChartActions
				setIsVisible={setIsVisible}
				setIsLoading={setIsLoading}
				isByExecution={isByExecution}
				canGroupByExecution={canGroupByExecution}
				toggleByExecution={toggleByExecution}
			/>
		}}
		chartProps={{
			enabledPDF: false,
			numberFormat: `#,###.00`,
			dateFormatReceived: 'yyyyMMdd',
			xAxis: isByExecution
				? {
					categoryField: 'date',
					title: xTitle,
				}
				: {
					categoryField: 'date',
					isDateAxis: true,
					dateFormat: 'dd MMM yyyy (EEE)',
					title: xTitle,
					baseInterval: {
						timeUnit: 'day',
						count: 1
					}
				},
			yAxis: isRankChart
				? {
					title: 'Market rank (1 = cheapest)',
					min: .5,
					max: maxRank + .5,
					maxPrecision: 0,
					// 1 on top, so a better position sits higher on the chart.
					renderer: { inversed: true },
				}
				: { title: isRating ? 'Rating' : 'Prices' },
		}}
	/>
}